(function () {
  "use strict";

  if (window.CoveoLocationService) return;

  const STORAGE_PREFIX = "coveo_location_";
  const KEYS = {
    LAT: STORAGE_PREFIX + "lat",
    LNG: STORAGE_PREFIX + "lng",
    TYPE: STORAGE_PREFIX + "type",
    ZIP: STORAGE_PREFIX + "zip",
    APPROX_ZIP: STORAGE_PREFIX + "approx_zip",
    CITY: STORAGE_PREFIX + "city",
    STATE: STORAGE_PREFIX + "state",
    TIMESTAMP: STORAGE_PREFIX + "timestamp",
    PRECISION: STORAGE_PREFIX + "precision",
    RADIUS_MILES: STORAGE_PREFIX + "radius_miles",
  };

  /** Radius at or above this value means nationwide — omit location from Coveo context. */
  const NATIONWIDE_RADIUS_MILES = 10000;
  const VALID_COMMERCE_RADIUS_MILES = [50, 100, 250, 500];
  const COOKIE_MAX_AGE_DAYS = 30;

  function isValidCommerceRadiusMiles(radius) {
    const r = Number(radius);
    return VALID_COMMERCE_RADIUS_MILES.indexOf(r) !== -1;
  }

  function hasGlobalPrivacyControl() {
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.globalPrivacyControl === true
      )
        return true;
      if (typeof window !== "undefined" && window.GPC === true) return true;
    } catch (_) {}
    return false;
  }

  function getCookieDomain() {
    try {
      if (typeof location === "undefined" || !location.hostname) return "";
      const host = String(location.hostname).trim();
      if (!host || host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        return "";
      }
      const parts = host.split(".").filter(Boolean);
      if (parts.length < 2) return "";
      return "." + parts.slice(-2).join(".");
    } catch (_) {}
    return "";
  }

  function getCookieString(name) {
    try {
      const cookieStr = typeof document !== "undefined" ? document.cookie || "" : "";
      if (!cookieStr) return "";
      const prefix = encodeURIComponent(name) + "=";
      const match = cookieStr.split(/;\s*/).find(function (entry) {
        return entry.indexOf(prefix) === 0;
      });
      if (!match) return "";
      return decodeURIComponent(match.slice(prefix.length));
    } catch (_) {}
    return "";
  }

  function setCookieString(name, value, options) {
    try {
      if (typeof document === "undefined") return;
      const opts = options || {};
      let cookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);
      cookie += "; path=/";
      cookie += "; SameSite=Lax";
      if (typeof location !== "undefined" && location.protocol === "https:") {
        cookie += "; Secure";
      }
      if (opts.maxAgeSeconds != null) {
        cookie += "; max-age=" + String(Math.max(0, Math.floor(opts.maxAgeSeconds)));
      }
      if (opts.domain) {
        cookie += "; domain=" + opts.domain;
      }
      document.cookie = cookie;
    } catch (_) {}
  }

  function removeCookieString(name) {
    const domain = getCookieDomain();
    setCookieString(name, "", { maxAgeSeconds: 0 });
    if (domain) {
      setCookieString(name, "", { maxAgeSeconds: 0, domain: domain });
    }
  }

  function parseOneTrustGroups(value) {
    if (!value) return [];
    return String(value)
      .split(/[,:|]/)
      .map(function (entry) {
        return String(entry || "").trim();
      })
      .filter(Boolean);
  }

  function getOneTrustConsentGroups() {
    try {
      if (typeof window !== "undefined") {
        const winGroups =
          window.OptanonActiveGroups ||
          window.OnetrustActiveGroups ||
          window.OneTrustActiveGroups;
        if (winGroups) {
          const parsedWinGroups = parseOneTrustGroups(winGroups);
          if (parsedWinGroups.length) return parsedWinGroups;
        }
      }
    } catch (_) {}

    const rawConsent = getCookieString("OptanonConsent");
    if (!rawConsent) return [];
    try {
      const params = new URLSearchParams(rawConsent);
      const groups =
        params.get("groups") ||
        params.get("Groups") ||
        params.get("activegroups") ||
        params.get("ActiveGroups") ||
        "";
      const parsed = parseOneTrustGroups(groups);
      if (parsed.length) return parsed;
    } catch (_) {}

    const groupsMatch = String(rawConsent).match(/(?:groups|Groups)=([^&]+)/);
    if (groupsMatch && groupsMatch[1]) {
      try {
        const parsed = parseOneTrustGroups(decodeURIComponent(groupsMatch[1]));
        if (parsed.length) return parsed;
      } catch (_) {}
    }
    return [];
  }

  function hasOneTrustPerformanceConsent() {
    const groups = getOneTrustConsentGroups();
    if (!groups.length) return false;
    return groups.some(function (group) {
      return /^c0*00?2$/i.test(group) || /^c0*00?3$/i.test(group);
    });
  }

  function shouldPersistLocationInCookies() {
    return !hasGlobalPrivacyControl() && hasOneTrustPerformanceConsent();
  }

  function getPersistentStorageBackend() {
    if (shouldPersistLocationInCookies()) {
      return { kind: "cookie" };
    }
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        return { kind: "localStorage", storage: window.localStorage };
      }
    } catch (_) {}
    return { kind: "sessionStorage", storage: window.sessionStorage };
  }

  function readPersistentValue(key) {
    const backend = getPersistentStorageBackend();
    try {
      if (backend.kind === "cookie") {
        return getCookieString(key) || null;
      }
      return backend.storage ? backend.storage.getItem(key) : null;
    } catch (_) {
      return null;
    }
  }

  function writePersistentValue(key, value) {
    const backend = getPersistentStorageBackend();
    try {
      if (backend.kind === "cookie") {
        if (value == null || String(value).trim() === "") {
          removeCookieString(key);
          return;
        }
        setCookieString(key, String(value).trim(), {
          maxAgeSeconds: COOKIE_MAX_AGE_DAYS * 86400,
          domain: getCookieDomain(),
        });
        return;
      }
      if (!backend.storage) return;
      persistString(backend.storage, key, value);
    } catch (_) {}
  }

  function clearPersistentLocationValues() {
    try {
      Object.values(KEYS).forEach(function (key) {
        removeCookieString(key);
      });
    } catch (_) {}
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        Object.values(KEYS).forEach(function (key) {
          window.localStorage.removeItem(key);
        });
      }
    } catch (_) {}
  }

  function getStorage(usePersistent) {
    try {
      if (usePersistent === false) return window.sessionStorage;
      const backend = getPersistentStorageBackend();
      if (backend.kind === "localStorage" && backend.storage) {
        return backend.storage;
      }
      if (backend.kind === "sessionStorage" && backend.storage) {
        return backend.storage;
      }
    } catch (_) {}
    return window.sessionStorage;
  }

  function parseStoredRadiusMiles(rawValue) {
    if (rawValue == null || String(rawValue).trim() === "") return null;
    const parsed = parseInt(rawValue, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  function getStoredRadiusMiles(usePersistent) {
    try {
      const persistentAllowed = usePersistent !== false;
      const sessionStorageRef = getStorage(false);
      const rawRadius =
        sessionStorageRef.getItem(KEYS.RADIUS_MILES) ||
        (persistentAllowed ? readPersistentValue(KEYS.RADIUS_MILES) : null);
      return parseStoredRadiusMiles(rawRadius);
    } catch (_) {
      return null;
    }
  }

  function getStoredLocation(usePersistent) {
    try {
      const persistentAllowed = usePersistent !== false;
      const sessionStorageRef = getStorage(false);
      const lat =
        sessionStorageRef.getItem(KEYS.LAT) ||
        (persistentAllowed ? readPersistentValue(KEYS.LAT) : null);
      const lng =
        sessionStorageRef.getItem(KEYS.LNG) ||
        (persistentAllowed ? readPersistentValue(KEYS.LNG) : null);
      if (lat == null || lng == null) return null;
      const numLat = parseFloat(lat);
      const numLng = parseFloat(lng);
      if (Number.isNaN(numLat) || Number.isNaN(numLng)) return null;
      const readValue = function (key) {
        return (
          sessionStorageRef.getItem(key) ||
          (persistentAllowed ? readPersistentValue(key) : null)
        );
      };
      const approxZipRaw = readValue(KEYS.APPROX_ZIP);
      return {
        lat: numLat,
        lng: numLng,
        type: readValue(KEYS.TYPE) || "geo",
        zip: readValue(KEYS.ZIP) || undefined,
        approxZip: approxZipRaw || undefined,
        city: readValue(KEYS.CITY) || undefined,
        state: readValue(KEYS.STATE) || undefined,
        timestamp: readValue(KEYS.TIMESTAMP) || undefined,
        precision: readValue(KEYS.PRECISION) || "approx",
        radiusMiles: getStoredRadiusMiles(persistentAllowed),
      };
    } catch (_) {
      return null;
    }
  }

  /**
   * Persist a single string-valued key into a storage instance, removing the
   * key when the value is empty/null. Keeps both `localStorage` and
   * `sessionStorage` writes consistent so we don't have to repeat the logic.
   */
  function persistString(storage, key, value) {
    if (value == null || String(value).trim() === "") {
      storage.removeItem(key);
    } else {
      storage.setItem(key, String(value).trim());
    }
  }

  function setStoredLocation(data) {
    try {
      const sessionStorageRef = getStorage(false);
      const timestamp = data.timestamp || new Date().toISOString();
      const hasRadiusMiles = Object.prototype.hasOwnProperty.call(
        data,
        "radiusMiles",
      );
      const radiusValue =
        hasRadiusMiles &&
        data.radiusMiles != null &&
        !Number.isNaN(Number(data.radiusMiles))
          ? String(parseInt(data.radiusMiles, 10))
          : null;
      const existingPersistentRadiusValue = hasRadiusMiles
        ? null
        : readPersistentValue(KEYS.RADIUS_MILES);
      sessionStorageRef.setItem(KEYS.LAT, String(data.lat));
      sessionStorageRef.setItem(KEYS.LNG, String(data.lng));
      sessionStorageRef.setItem(KEYS.TYPE, data.type || "geo");
      persistString(sessionStorageRef, KEYS.ZIP, data.zip);
      if (data.type === "zip") {
        sessionStorageRef.removeItem(KEYS.APPROX_ZIP);
      } else {
        persistString(sessionStorageRef, KEYS.APPROX_ZIP, data.approxZip);
      }
      persistString(sessionStorageRef, KEYS.CITY, data.city);
      persistString(sessionStorageRef, KEYS.STATE, data.state);
      sessionStorageRef.setItem(KEYS.TIMESTAMP, timestamp);
      sessionStorageRef.setItem(KEYS.PRECISION, data.precision || "approx");
      if (hasRadiusMiles) {
        persistString(sessionStorageRef, KEYS.RADIUS_MILES, radiusValue);
      }

      clearPersistentLocationValues();
      writePersistentValue(KEYS.LAT, String(data.lat));
      writePersistentValue(KEYS.LNG, String(data.lng));
      writePersistentValue(KEYS.TYPE, data.type || "geo");
      writePersistentValue(KEYS.ZIP, data.zip);
      writePersistentValue(
        KEYS.APPROX_ZIP,
        data.type === "zip" ? null : data.approxZip,
      );
      writePersistentValue(KEYS.CITY, data.city);
      writePersistentValue(KEYS.STATE, data.state);
      writePersistentValue(KEYS.TIMESTAMP, timestamp);
      writePersistentValue(KEYS.PRECISION, data.precision || "approx");
      writePersistentValue(
        KEYS.RADIUS_MILES,
        hasRadiusMiles ? radiusValue : existingPersistentRadiusValue,
      );
    } catch (_) {}
  }

  function setStoredRadiusMiles(radiusMiles) {
    try {
      const sessionStorageRef = getStorage(false);
      const radiusValue =
        radiusMiles != null && !Number.isNaN(Number(radiusMiles))
          ? String(parseInt(radiusMiles, 10))
          : null;
      persistString(sessionStorageRef, KEYS.RADIUS_MILES, radiusValue);
      writePersistentValue(KEYS.RADIUS_MILES, radiusValue);
    } catch (_) {}
  }

  function clearStoredRadiusMiles() {
    /*
     * Consent can switch the persistent backend between localStorage and a
     * cookie during the same visit. Clearing only the backend that is active
     * right now leaves a stale radius in the previous backend, which then
     * resurrects the distance filter on the next render/page load.
     *
     * Remove only the radius key from every supported backend; the saved ZIP
     * and coordinates stay available for Nearest sorting.
     */
    try {
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.removeItem(KEYS.RADIUS_MILES);
      }
    } catch (_) {}
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.removeItem(KEYS.RADIUS_MILES);
      }
    } catch (_) {}
    try {
      removeCookieString(KEYS.RADIUS_MILES);
    } catch (_) {}
  }

  function clearStoredLocation() {
    try {
      const keys = Object.values(KEYS);
      [window.sessionStorage].forEach(function (s) {
        keys.forEach(function (k) {
          s.removeItem(k);
        });
      });
      clearPersistentLocationValues();
    } catch (_) {}
  }

  function getBrowserLocation(timeout) {
    const t = timeout != null ? timeout : 10000;
    return new Promise(function (resolve, reject) {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        reject(new Error("Geolocation is not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        function (err) {
          const code = err && typeof err.code === "number" ? err.code : 0;
          const msg =
            code === 1
              ? "Permission denied"
              : code === 2
                ? "Position unavailable"
                : "Timeout or error";
          const e = new Error(msg);
          e._geolocationCode = code;
          reject(e);
        },
        { enableHighAccuracy: false, timeout: t, maximumAge: 5 * 60 * 1000 },
      );
    });
  }

  /**
   * Pull a 2-letter US state abbreviation out of an OSM-style `ISO3166-2-lvl4`
   * tag (e.g. "US-FL" -> "FL"). Falls back to the first 2 chars of state name
   * when no ISO tag is present (best effort, never throws).
   */
  function deriveStateAbbr(addr) {
    if (!addr) return "";
    const iso = addr["ISO3166-2-lvl4"] || addr.iso3166_2 || "";
    if (typeof iso === "string" && iso.indexOf("-") !== -1) {
      const tail = iso.split("-").pop() || "";
      if (tail.length === 2) return tail.toUpperCase();
    }
    if (typeof addr.state_code === "string" && addr.state_code.length <= 3) {
      return addr.state_code.toUpperCase();
    }
    return "";
  }

  /**
   * Pick the best human-readable city/town/village name from an OSM address
   * object. Different countries surface the locality on different fields.
   */
  function deriveCityName(addr) {
    if (!addr) return "";
    return (
      addr.city ||
      addr.town ||
      addr.village ||
      addr.hamlet ||
      addr.suburb ||
      addr.municipality ||
      addr.county ||
      ""
    );
  }

  function pickFirstZipToken(raw) {
    const s = raw != null ? String(raw).trim() : "";
    if (!s) return "";
    return s.split(/[\s,-]/)[0] || s;
  }

  /**
   * Full reverse-geocode used by the new distance filter UI (Figma 2026-05).
   * Returns `{ zip, city, state }` — any missing field is an empty string.
   */
  function reverseGeocodeAddress(lat, lng) {
    const numLat = Number(lat);
    const numLng = Number(lng);
    if (Number.isNaN(numLat) || Number.isNaN(numLng)) {
      return Promise.resolve({ zip: "", city: "", state: "" });
    }
    const params = new URLSearchParams({
      format: "json",
      lat: String(numLat),
      lon: String(numLng),
      zoom: "14",
      addressdetails: "1",
    });
    const url =
      "https://nominatim.openstreetmap.org/reverse?" + params.toString();
    return fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        if (!r.ok) throw new Error("Reverse geocode failed");
        return r.json();
      })
      .then(function (data) {
        const addr = (data && data.address) || {};
        const pc =
          addr.postcode ||
          addr.postal_code ||
          (Array.isArray(addr.postcodes) ? addr.postcodes[0] : "");
        return {
          zip: pickFirstZipToken(pc),
          city: deriveCityName(addr),
          state: deriveStateAbbr(addr),
        };
      })
      .catch(function () {
        return { zip: "", city: "", state: "" };
      });
  }

  /**
   * Back-compat: string-only reverse geocode. Kept so existing callers that
   * destructure the zipcode keep working.
   */
  function reverseGeocode(lat, lng) {
    return reverseGeocodeAddress(lat, lng).then(function (info) {
      return info.zip || "";
    });
  }

  function geocodeZip(zip, options) {
    const trimmed = (zip || "").trim();
    if (!trimmed)
      return Promise.reject(new Error("ZIP code is required"));

    const geocodeUrl = (options && options.geocodeUrl) || "";
    const country = (options && options.country) || "US";

    if (geocodeUrl) {
      const url =
        geocodeUrl +
        encodeURIComponent(trimmed) +
        (country ? "&country=" + encodeURIComponent(country) : "");
      return fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error("Geocoding failed");
          return r.json();
        })
        .then(function (data) {
          const lat = data.lat != null ? parseFloat(data.lat) : NaN;
          const lng = data.lng != null ? parseFloat(data.lng) : NaN;
          if (Number.isNaN(lat) || Number.isNaN(lng))
            throw new Error("Invalid response");
          return { lat, lng };
        });
    }

    const countryCode = String(country || "US").toLowerCase();

    if (countryCode === "us") {
      const zipApiUrl =
        "https://api.zippopotam.us/us/" + encodeURIComponent(trimmed);
      return fetch(zipApiUrl, {
        method: "GET",
        headers: { Accept: "application/json" },
      })
        .then(function (r) {
          if (!r.ok) throw new Error("ZIP API failed");
          return r.json();
        })
        .then(function (data) {
          var places = Array.isArray(data && data.places) ? data.places : [];
          if (!places.length) throw new Error("ZIP code not found");
          var first = places[0] || {};
          const lat = parseFloat(first.latitude);
          const lng = parseFloat(first.longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng))
            throw new Error("Invalid response");
          // zippopotam.us also exposes `place name` + `state abbreviation`;
          // pass them along so the distance filter pill can show
          // "City, ST 34677" without an extra round-trip to Nominatim.
          return {
            lat,
            lng,
            city: first["place name"] || "",
            state: first["state abbreviation"] || "",
          };
        })
        .catch(function () {
          const params = new URLSearchParams({
            postalcode: trimmed,
            country: "US",
            format: "json",
            limit: "1",
            addressdetails: "1",
          });
          const nominatimUrl =
            "https://nominatim.openstreetmap.org/search?" + params.toString();
          return fetch(nominatimUrl, {
            method: "GET",
            headers: { Accept: "application/json" },
          })
            .then(function (r) {
              if (!r.ok) throw new Error("Geocoding failed");
              return r.json();
            })
            .then(function (arr) {
              if (!Array.isArray(arr) || arr.length === 0) {
                throw new Error("ZIP code not found");
              }
              const first = arr[0];
              const lat = parseFloat(first.lat);
              const lng = parseFloat(first.lon);
              if (Number.isNaN(lat) || Number.isNaN(lng))
                throw new Error("Invalid response");
              const addr = first.address || {};
              return {
                lat,
                lng,
                city: deriveCityName(addr),
                state: deriveStateAbbr(addr),
              };
            });
        });
    }

    const params = new URLSearchParams({
      postalcode: trimmed,
      country: country,
      format: "json",
      limit: "1",
    });
    const url =
      "https://nominatim.openstreetmap.org/search?" + params.toString();
    return fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        if (!r.ok) throw new Error("Geocoding failed");
        return r.json();
      })
      .then(function (arr) {
        if (!Array.isArray(arr) || arr.length === 0)
          throw new Error("ZIP code not found");
        const first = arr[0];
        const lat = parseFloat(first.lat);
        const lng = parseFloat(first.lon);
        if (Number.isNaN(lat) || Number.isNaN(lng))
          throw new Error("Invalid response");
        return { lat, lng };
      });
  }

  function geocodeAddress(address, options) {
    const q = (address || "").trim();
    if (!q) return Promise.reject(new Error("Address is required"));

    const geocodeUrl = (options && options.geocodeUrl) || "";
    const countrycodes = (
      (options && options.countrycodes) ||
      "us"
    ).toLowerCase();

    if (geocodeUrl) {
      const url =
        geocodeUrl +
        encodeURIComponent(q) +
        (countrycodes
          ? "&countrycodes=" + encodeURIComponent(countrycodes)
          : "");
      return fetch(url)
        .then(function (r) {
          if (!r.ok) throw new Error("Geocoding failed");
          return r.json();
        })
        .then(function (data) {
          const lat = data.lat != null ? parseFloat(data.lat) : NaN;
          const lng = data.lng != null ? parseFloat(data.lng) : NaN;
          if (Number.isNaN(lat) || Number.isNaN(lng))
            throw new Error("Invalid response");
          return { lat, lng };
        });
    }

    const params = new URLSearchParams({
      q: q,
      format: "json",
      limit: "1",
      countrycodes: countrycodes,
    });
    const url =
      "https://nominatim.openstreetmap.org/search?" + params.toString();
    return fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        if (!r.ok) throw new Error("Geocoding failed");
        return r.json();
      })
      .then(function (arr) {
        if (!Array.isArray(arr) || arr.length === 0)
          throw new Error("Address not found");
        const first = arr[0];
        const lat = parseFloat(first.lat);
        const lng = parseFloat(first.lon);
        if (Number.isNaN(lat) || Number.isNaN(lng))
          throw new Error("Invalid response");
        return { lat, lng };
      });
  }

  function requestLocation(radiusMiles, timeout) {
    const radius = radiusMiles != null ? radiusMiles : 50;
    return getBrowserLocation(timeout).then(function (coords) {
      return reverseGeocodeAddress(coords.lat, coords.lng).then(
        function (info) {
          const data = {
            lat: coords.lat,
            lng: coords.lng,
            type: "geo",
            zip: undefined,
            approxZip: info.zip || undefined,
            city: info.city || undefined,
            state: info.state || undefined,
            timestamp: new Date().toISOString(),
            precision: "device",
            radiusMiles: radius,
          };
          setStoredLocation(data);
          setStoredRadiusMiles(radius);
          return data;
        },
      );
    });
  }

  function setLocationFromZip(zip, options) {
    const radius =
      (options && options.radiusMiles) != null ? options.radiusMiles : 25;
    return geocodeZip(zip, options).then(function (coords) {
      const data = {
        lat: coords.lat,
        lng: coords.lng,
        type: "zip",
        zip: (zip || "").trim(),
        city: coords.city || undefined,
        state: coords.state || undefined,
        timestamp: new Date().toISOString(),
        precision: "approx",
        radiusMiles: radius,
      };
      setStoredLocation(data);
      setStoredRadiusMiles(radius);
      return data;
    });
  }

  /**
   * Merge stored user location into a Coveo commerce search/listing request body.
   * @returns {boolean} true when context was updated
   */
  function isCoveoCommerceRequestBody(body) {
    if (!body || typeof body !== "object" || Array.isArray(body)) return false;
    return (
      body.query !== undefined ||
      body.context !== undefined ||
      Object.prototype.hasOwnProperty.call(body, "cq") ||
      body.facets !== undefined ||
      body.page !== undefined ||
      body.pageSize !== undefined ||
      body.perPage !== undefined ||
      body.numberOfResults !== undefined ||
      body.sort !== undefined
    );
  }

  function mergeLocationContextIntoCommerceContext(context, locCtx) {
    if (!context || typeof context !== "object" || !locCtx) return context;
    const next = Object.assign({}, context, locCtx);
    if (locCtx.location) {
      next.location = Object.assign({}, context.location || {}, locCtx.location);
    }
    const customFields = {
      location_type: locCtx.location_type,
      location_lat: locCtx.location_lat,
      location_lng: locCtx.location_lng,
      location_zip: locCtx.location_zip,
      location_timestamp: locCtx.location_timestamp,
      location_precision: locCtx.location_precision,
      location_radius_miles: locCtx.location_radius_miles,
    };
    next.custom = Object.assign({}, context.custom || {}, customFields);
    return next;
  }

  function applyLocationContextToRequestBody(body) {
    if (!isCoveoCommerceRequestBody(body)) return false;
    if (
      typeof window !== "undefined" &&
      window.__coveoClientOnlyDistanceFilter !== false
    ) {
      return false;
    }
    const locCtx = getContextForCoveo();
    if (!locCtx) return false;
    if (typeof body.context === "object") {
      body.context = mergeLocationContextIntoCommerceContext(body.context, locCtx);
    } else {
      body.context = mergeLocationContextIntoCommerceContext({}, locCtx);
    }
    return true;
  }

  function buildPlaceGeocodeKey(opts) {
    if (!opts || typeof opts !== "object") return "";
    var place = (opts.place || "").trim();
    var city = (opts.city || "").trim();
    var state = (opts.state || "").trim();
    if (place) return "place:" + place.toLowerCase();
    if (city && state) return "cs:" + city.toLowerCase() + "," + state.toLowerCase();
    if (city) return "city:" + city.toLowerCase();
    return "";
  }

  function buildPlaceGeocodeQuery(opts) {
    if (!opts || typeof opts !== "object") return "";
    var place = (opts.place || "").trim();
    var city = (opts.city || "").trim();
    var state = (opts.state || "").trim();
    if (place) return place + ", USA";
    if (city && state) return city + ", " + state + ", USA";
    if (city) return city + ", USA";
    return "";
  }

  var placeGeocodeChain = Promise.resolve();
  var lastPlaceGeocodeAt = 0;

  function enqueuePlaceGeocode(task) {
    placeGeocodeChain = placeGeocodeChain
      .then(function () {
        var minGapMs = 1100;
        var wait = Math.max(0, minGapMs - (Date.now() - lastPlaceGeocodeAt));
        if (wait > 0) {
          return new Promise(function (resolve) {
            setTimeout(resolve, wait);
          });
        }
      })
      .then(function () {
        lastPlaceGeocodeAt = Date.now();
        return task();
      })
      .catch(function () {
        return null;
      });
    return placeGeocodeChain;
  }

  /** Resolve dealer location from city/state or location name (cached, rate-limited). */
  function resolvePlaceCoordinates(opts) {
    var key = buildPlaceGeocodeKey(opts);
    if (!key) return Promise.resolve(null);
    if (!window.__coveoPlaceGeoCache) {
      window.__coveoPlaceGeoCache = new Map();
    }
    var cache = window.__coveoPlaceGeoCache;
    if (cache.has(key)) return Promise.resolve(cache.get(key));

    var query = buildPlaceGeocodeQuery(opts);
    if (!query) return Promise.resolve(null);

    return enqueuePlaceGeocode(function () {
      return geocodeAddress(query, { countrycodes: "us" }).then(function (coords) {
        var entry = { lat: coords.lat, lng: coords.lng };
        cache.set(key, entry);
        return entry;
      });
    });
  }

  function haversineDistanceMiles(lat1, lng1, lat2, lng2) {
    const a1 = Number(lat1);
    const o1 = Number(lng1);
    const a2 = Number(lat2);
    const o2 = Number(lng2);
    if (
      !Number.isFinite(a1) ||
      !Number.isFinite(o1) ||
      !Number.isFinite(a2) ||
      !Number.isFinite(o2)
    ) {
      return NaN;
    }
    const R = 3959;
    const dLat = ((a2 - a1) * Math.PI) / 180;
    const dLng = ((o2 - o1) * Math.PI) / 180;
    const x =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((a1 * Math.PI) / 180) *
        Math.cos((a2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    return R * c;
  }

  function getContextForCoveo(radiusMiles) {
    const loc = getStoredLocation(true);
    if (!loc) return null;
    let radius =
      radiusMiles != null && !Number.isNaN(Number(radiusMiles))
        ? Number(radiusMiles)
        : loc.radiusMiles != null && !Number.isNaN(Number(loc.radiusMiles))
          ? Number(loc.radiusMiles)
          : null;
    if (
      typeof window !== "undefined" &&
      window.__coveoSelectedRadiusMiles != null &&
      !Number.isNaN(Number(window.__coveoSelectedRadiusMiles))
    ) {
      radius = Number(window.__coveoSelectedRadiusMiles);
    }
    if (radius == null) {
      return null;
    }
    if (radius >= NATIONWIDE_RADIUS_MILES || !isValidCommerceRadiusMiles(radius)) {
      return null;
    }
    return {
      location: {
        latitude: loc.lat,
        longitude: loc.lng,
      },
      location_type: loc.type,
      location_lat: loc.lat,
      location_lng: loc.lng,
      location_zip: loc.zip || "",
      location_timestamp: loc.timestamp || "",
      location_precision: loc.precision,
      location_radius_miles: radius,
    };
  }

  window.CoveoLocationService = {
    NATIONWIDE_RADIUS_MILES: NATIONWIDE_RADIUS_MILES,
    getStoredLocation: getStoredLocation,
    getStoredRadiusMiles: getStoredRadiusMiles,
    setStoredLocation: setStoredLocation,
    setStoredRadiusMiles: setStoredRadiusMiles,
    clearStoredRadiusMiles: clearStoredRadiusMiles,
    clearStoredLocation: clearStoredLocation,
    getBrowserLocation: getBrowserLocation,
    reverseGeocode: reverseGeocode,
    reverseGeocodeAddress: reverseGeocodeAddress,
    geocodeZip: geocodeZip,
    geocodeAddress: geocodeAddress,
    requestLocation: requestLocation,
    setLocationFromZip: setLocationFromZip,
    getContextForCoveo: getContextForCoveo,
    applyLocationContextToRequestBody: applyLocationContextToRequestBody,
    isCoveoCommerceRequestBody: isCoveoCommerceRequestBody,
    haversineDistanceMiles: haversineDistanceMiles,
    buildPlaceGeocodeKey: buildPlaceGeocodeKey,
    resolvePlaceCoordinates: resolvePlaceCoordinates,
    hasGlobalPrivacyControl: hasGlobalPrivacyControl,
    KEYS: KEYS,
  };
})();
